import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import Markdown from "markdown-to-jsx";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { ArrowDownward } from "@mui/icons-material";
import { useStore } from "utils/useStore";
import { useReadToBottom } from "components/Classifiers/LLMClassifiers/utils/useReadToBottom";
import KboUserAgreementStore from "../Stores/KboUserAgreementStore";
import type { KboUserAgreementDocument } from "../Models/KboUserAgreement";

const SectionHeading = ({ children }: { children?: React.ReactNode }) => (
    <Typography
        component="h3"
        sx={(theme) => ({
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            color: theme.palette.gray[700],
            marginTop: 2,
            marginBottom: 0.5,
            ":first-of-type": { marginTop: 0 },
        })}
    >
        {children}
    </Typography>
);

const Paragraph = ({ children }: { children?: React.ReactNode }) => (
    <Typography
        sx={(theme) => ({
            fontSize: "14px",
            lineHeight: "20px",
            color: theme.palette.gray[600],
            marginBottom: 1.5,
            ":last-child": { marginBottom: 0 },
        })}
    >
        {children}
    </Typography>
);

const List =
    (component: "ul" | "ol") =>
    ({ children }: { children?: React.ReactNode }) => (
        <Box
            component={component}
            sx={(theme) => ({
                marginTop: 0,
                marginBottom: 1.5,
                paddingLeft: 3,
                fontSize: "14px",
                lineHeight: "20px",
                color: theme.palette.gray[600],
                ":last-child": { marginBottom: 0 },
                "& > li": { marginBottom: 0.5 },
            })}
        >
            {children}
        </Box>
    );

const BulletedList = List("ul");
const NumberedList = List("ol");

type Props = {
    agreement: KboUserAgreementDocument;
};

/**
 * The agreement in a box the user has to scroll to the end of before they can
 * agree. Agreements short enough to fit count as read straight away.
 */
export const KboUserAgreementTerms = observer(({ agreement }: Props) => {
    const store = useStore(KboUserAgreementStore);
    const theme = useTheme();
    // A callback ref, so the read check starts as soon as the box exists.
    const [scroller, setScroller] = useState<HTMLDivElement | null>(null);

    const hasReachedEnd = useReadToBottom(scroller, agreement.revision, true);

    useEffect(() => {
        if (hasReachedEnd) store.markAgreementRead();
    }, [hasReachedEnd, store]);

    // Pick up where they left off if the box was closed part way through. New text
    // starts from the top: left at the end of the old text, it would count as read
    // without being read.
    useEffect(() => {
        if (scroller) scroller.scrollTop = store.savedScrollTop;
    }, [scroller, store, agreement.revision]);

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                ref={setScroller}
                id="kbo-user-agreement-terms"
                role="region"
                aria-label={`${agreement.title}, version ${agreement.version}`}
                tabIndex={0}
                onScroll={(event) =>
                    store.rememberScrollTop(event.currentTarget.scrollTop)
                }
                sx={{
                    maxHeight: "260px",
                    overflowY: "auto",
                    padding: 2,
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: `${theme.shape.borderRadius}px`,
                    backgroundColor: theme.palette.gray[50],
                    ":focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: "2px",
                    },
                }}
            >
                <Markdown
                    options={{
                        disableParsingRawHTML: true,
                        overrides: {
                            h1: SectionHeading,
                            h2: SectionHeading,
                            h3: SectionHeading,
                            p: Paragraph,
                            ul: BulletedList,
                            ol: NumberedList,
                        },
                    }}
                >
                    {agreement.content}
                </Markdown>
                <Typography
                    sx={{
                        marginTop: 2,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: theme.palette.gray[500],
                    }}
                >
                    End of agreement · Version {agreement.version}
                </Typography>
            </Box>
            {!store.hasReadAgreement && (
                <ButtonBase
                    id="kbo-user-agreement-keep-scrolling"
                    onClick={() =>
                        scroller?.scrollBy({
                            top: scroller.clientHeight * 0.8,
                            behavior: "smooth",
                        })
                    }
                    sx={{
                        position: "absolute",
                        bottom: theme.spacing(1.5),
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        paddingBlock: 0.5,
                        paddingInline: 1.5,
                        borderRadius: "9999px",
                        border: `1px solid ${theme.palette.gray[200]}`,
                        backgroundColor: theme.palette.white.main,
                        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.10)",
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: theme.palette.gray[700],
                    }}
                >
                    <ArrowDownward sx={{ fontSize: "14px" }} />
                    Keep scrolling to finish reading
                </ButtonBase>
            )}
        </Box>
    );
});
